import { PaginatedQueryBase } from '@services/api'
import { ActionReducerMapBuilder, createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { castDraft } from 'immer'
import { PaginatedDto } from '@services/extendedType'

export interface CrudService<
  TEntity extends { id: string | number },
  TCreateDto,
  TUpdateDto,
  TFilters extends PaginatedQueryBase
> {
  getAll(filters: TFilters): Promise<PaginatedDto<TEntity>>
  create(dto: TCreateDto): Promise<TEntity>
  update(id: TEntity['id'], dto: TUpdateDto): Promise<TEntity>
  remove(id: TEntity['id']): Promise<void>
}

export const DEFAULT_FILTERS = { limit: 20, page: 0 }

export interface CrudState<
  TEntity extends { id: string | number },
  TFilters extends PaginatedQueryBase
> {
  items: PaginatedDto<TEntity>
  filters: TFilters
  loading: boolean
}

export function baseCrudReducer<
  TEntity extends { id: string | number },
  TCreateDto,
  TUpdateDto,
  TFilters extends PaginatedQueryBase
>(
  name: string,
  service: CrudService<TEntity, TCreateDto, TUpdateDto, TFilters>,
  options?: {
    selectFilters?: (state: any) => TFilters
    extraReducers?: (builder: ActionReducerMapBuilder<CrudState<TEntity, TFilters>>) => void
  }
) {
  const getAll = createAsyncThunk<PaginatedDto<TEntity>, void>(
    `${name}/find`,
    async (_, { getState }) => {
      const state = getState() as any
      const filters: TFilters =
        options?.selectFilters?.(state) ??
        state?.[name]?.filters ??
        (DEFAULT_FILTERS as TFilters)
      return service.getAll(filters)
    },
  )

  const createItem = createAsyncThunk<TEntity, TCreateDto>(
    `${name}/create`,
    async (dto, { dispatch }) => {
      const created = await service.create(dto)
      await dispatch(getAll())
      return created
    },
  )

  const updateItem = createAsyncThunk<TEntity, { id: string; dto: TUpdateDto }>(
    `${name}/update`,
    async ({ id, dto }, { dispatch }) => {
      const updated = await service.update(id, dto)
      await dispatch(getAll())
      return updated
    },
  )

  const removeItem = createAsyncThunk<string, string>(
    `${name}/remove`,
    async (id, { dispatch }) => {
      await service.remove(id)
      await dispatch(getAll())
      return id
    },
  )

  const initialState: CrudState<TEntity, TFilters> = {
    items: { datas: [] as TEntity[], totalCount: 0 },
    filters: DEFAULT_FILTERS as TFilters,
    loading: false,
  }

  const slice = createSlice({
    name,
    initialState,
    reducers: {
      setFilters: (state, action: PayloadAction<Partial<TFilters>>) => {
        state.filters = { ...state.filters, page: 0, ...action.payload }
      },
    },
    extraReducers: (b) => {
      b.addCase(getAll.pending, (state) => { state.loading = true })
        .addCase(getAll.rejected, (state) => { state.loading = false })
        .addCase(getAll.fulfilled, (state, action) => { state.items = castDraft(action.payload); state.loading = false })
      options?.extraReducers?.(b)
    },
  })

  return {
    reducer: slice.reducer,
    actions: slice.actions,
    thunks: { getAll, create: createItem, update: updateItem, remove: removeItem },
  }
}
